import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Share2, Check, Copy, Twitter, Facebook, Linkedin, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Share button with multi-platform popover + rich preview links.
 * Variants:
 *   - hero      → big neon CTA, meant for hero/above-the-fold placement
 *   - primary   → default bordered button
 *   - secondary → compact bordered button
 *   - card      → tiny icon-only pill, used on marketplace cards
 */
export default function ShareButton({
  url,
  shareUrl,
  title,
  description,
  testId,
  variant = "primary",
  stopPropagation = false,
}) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const { user } = useAuth() || {};

  // Auto-attach referral tag when a logged-in user shares. `ref` param is
  // read by ProductDetail on load and forwarded to the checkout endpoint.
  const withRef = (u) => {
    if (!u || !user?.user_id) return u;
    try {
      const parsed = new URL(u, typeof window !== "undefined" ? window.location.origin : "http://x");
      if (!parsed.searchParams.get("ref")) parsed.searchParams.set("ref", user.user_id);
      return parsed.toString();
    } catch {
      const sep = u.includes("?") ? "&" : "?";
      return `${u}${sep}ref=${encodeURIComponent(user.user_id)}`;
    }
  };
  const trackedUrl = withRef(url);
  const trackedShareUrl = withRef(shareUrl);
  const rich = trackedShareUrl || trackedUrl;
  const safeTitle = title || "";
  const safeDesc = description || "";

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const popoverWidth = 320;
    const left = Math.min(
      Math.max(8, r.right - popoverWidth),
      window.innerWidth - popoverWidth - 8
    );
    setPos({ top: r.bottom + window.scrollY + 8, left: left + window.scrollX });
  }, [open]);

  const handle = (e, fn) => {
    if (stopPropagation && e) { e.preventDefault(); e.stopPropagation(); }
    fn && fn();
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(rich);
    } catch {
      const el = document.createElement("textarea");
      el.value = rich;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const doShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ url: rich, title: safeTitle, text: safeDesc });
        return;
      } catch {
        // user cancelled or unsupported — fall through
      }
    }
    setOpen((v) => !v);
  };

  const encUrl = encodeURIComponent(rich);
  const encText = encodeURIComponent(safeTitle);
  const encBody = encodeURIComponent(`${safeTitle}\n\n${rich}`);

  const platforms = [
    { name: "X / Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`, testId: "share-twitter" },
    { name: "WhatsApp", icon: MessageCircle, href: `https://api.whatsapp.com/send?text=${encBody}`, testId: "share-whatsapp" },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`, testId: "share-facebook" },
    { name: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`, testId: "share-linkedin" },
    { name: "Reddit", icon: RedditIcon, href: `https://www.reddit.com/submit?url=${encUrl}&title=${encText}`, testId: "share-reddit" },
    { name: "Email", icon: Mail, href: `mailto:?subject=${encText}&body=${encBody}`, testId: "share-email" },
  ];

  const base = "inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] transition-all";
  const styles = {
    hero: `${base} bg-[#CCFF00] text-black text-xs px-5 py-3 hover:brightness-110 hover:scale-[1.02] shadow-[0_0_0_1px_rgba(204,255,0,0.4)]`,
    primary: `${base} border border-white/20 hover:border-white/50 text-xs px-4 py-2.5`,
    secondary: `${base} border border-white/15 hover:border-white/40 text-[10px] px-2 py-1.5`,
    card: `${base} bg-black/80 backdrop-blur border border-white/20 hover:border-[#CCFF00] hover:text-[#CCFF00] text-[10px] w-8 h-8 justify-center p-0`,
  };
  const iconSize = variant === "hero" ? "w-4 h-4" : variant === "card" ? "w-3.5 h-3.5" : "w-3.5 h-3.5";

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        data-testid={testId}
        onClick={(e) => handle(e, doShare)}
        className={styles[variant] || styles.primary}
        title="Share this drop"
        aria-label="Share this drop"
      >
        <Share2 className={iconSize} />
        {variant !== "card" && <span>Share</span>}
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={(e) => handle(e, () => setOpen(false))}
          />
          <div
            className="absolute z-[9999] w-[320px] bg-[#0A0A0A] border border-white/15 p-3 shadow-2xl"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 px-1 pb-2">
              // Share this drop · rich preview enabled
            </div>

            <button
              type="button"
              data-testid={testId ? `${testId}-copy` : "share-copy"}
              onClick={(e) => handle(e, doCopy)}
              className="w-full flex items-center justify-between gap-2 border border-white/10 hover:border-[#CCFF00] bg-[#050505] px-3 py-2 text-left transition-colors"
            >
              <span className="font-mono text-xs truncate text-white/80">{rich}</span>
              {copied ? (
                <Check className="w-4 h-4 text-[#CCFF00] shrink-0" />
              ) : (
                <Copy className="w-4 h-4 text-white/60 shrink-0" />
              )}
            </button>

            <div className="mt-3 grid grid-cols-3 gap-2">
              {platforms.map((p) => (
                <a
                  key={p.name}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testId ? `${testId}-${p.testId}` : p.testId}
                  onClick={(e) => e.stopPropagation()}
                  className="flex flex-col items-center gap-1.5 border border-white/10 hover:border-[#CCFF00] hover:text-[#CCFF00] px-2 py-2.5 font-mono text-[9px] uppercase tracking-[0.15em] text-white/70 transition-colors"
                  title={`Share on ${p.name}`}
                >
                  <p.icon className="w-4 h-4" />
                  {p.name}
                </a>
              ))}
            </div>

            {trackedUrl && trackedUrl !== rich && (
              <div className="mt-3 font-mono text-[10px] text-white/40 px-1">
                Opens: <span className="text-white/60 break-all">{trackedUrl}</span>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// Reddit doesn't ship with lucide-react — use a small inline SVG.
function RedditIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm5.4 11.1c.03.19.05.38.05.58 0 2.94-3.4 5.32-7.6 5.32s-7.6-2.38-7.6-5.32c0-.2.02-.39.05-.58a1.7 1.7 0 111.98-2.61c1.3-.83 3.03-1.36 4.94-1.44l1.03-4.63c.02-.09.08-.16.16-.2a.28.28 0 01.22-.02l3.35.72a1.4 1.4 0 111.15 1.95l-2.6-.56-.9 4.06c1.85.11 3.52.63 4.78 1.44a1.7 1.7 0 111.99 2.61zM8.7 14.3a1.1 1.1 0 100-2.2 1.1 1.1 0 000 2.2zm7.7-1.1a1.1 1.1 0 10-2.2 0 1.1 1.1 0 002.2 0zm-1.36 3.02a.36.36 0 00-.5-.02c-.55.5-1.5.83-2.54.83s-1.99-.33-2.54-.83a.36.36 0 10-.48.53c.7.63 1.81 1.02 3.02 1.02s2.32-.39 3.02-1.02a.36.36 0 00.02-.51z" />
    </svg>
  );
}
